import React from "react";
import "./style.css";

const MessagesPrivacyPolicy = () => {
  return (
    <div className="messagesPrivacyPolicyBody">
      <main className="messagesPrivacyPolicyMain">
        <h1>Privacy Policy for Messages: SMS & Texing App</h1>
        <p>Last Updated: August 24, 2026</p>
        <p>
          Welcome to Messages: SMS & Texing App (the "Application"). This Privacy Policy explains
          how we ("we," "our," or "us") collect, use, disclose, and safeguard
          your information when you use our Application. Our primary goal is to
          provide a secure, on-device messaging experience that respects your
          privacy.
        </p>

        <h2>1. Messages, Contacts &amp; Local Processing</h2>
        <p>
          Our Application lets you send and receive SMS and MMS messages, view
          your conversation history, schedule messages, and manage contacts. How
          we handle this data:
        </p>
        <ul>
          <li>
            <strong>100% Local Processing:</strong> All of your text messages,
            multimedia messages, call logs, and contacts are read, stored, and
            processed entirely on your device.
          </li>
          <li>
            <strong>No Cloud Uploads:</strong> We absolutely do NOT collect,
            upload, or transmit the content of your messages, contacts, or call
            logs to our servers or any third-party servers. Your conversations
            remain private between you and the people you message.
          </li>
          <li>
            <strong>Local Backup Only:</strong> The Application's Backup &amp;
            Restore feature saves a copy of your messages to a file on your own
            device's storage, at your request. This backup file is never
            uploaded anywhere by us — you control where it's stored and shared.
          </li>
        </ul>

        <h2>2. Device Permissions Required</h2>
        <p>
          To function as a full-featured messaging app (including as your
          device's default SMS app), our Application requires the following
          permissions. Each is used strictly for the feature described, never
          for unauthorized data collection:
        </p>
        <ul>
          <li>
            <strong>SMS &amp; MMS (Send, Receive, Read):</strong> Core
            permissions required to send, receive, and display text and
            multimedia messages. Required for the app to work as your default
            messaging app.
          </li>
          <li>
            <strong>Contacts:</strong> Used to match phone numbers to contact
            names and photos so your conversations display recognizable names
            instead of raw numbers, and to let you start new conversations from
            your contact list.
          </li>
          <li>
            <strong>Phone State &amp; Call Log:</strong> Used to detect when a
            call ends and to show call duration/caller details, powering the
            optional "After Call" feature (quick reply, call-back reminders) and
            letting you place calls directly from a conversation.
          </li>
          <li>
            <strong>Camera &amp; Photos/Media:</strong> Used so you can attach
            photos to an MMS message or take a new photo directly within a
            conversation.
          </li>
          <li>
            <strong>Notifications:</strong> Used to alert you about new messages
            and reminders.
          </li>
          <li>
            <strong>Display Over Other Apps:</strong> Used only for the optional
            "After Call" popup, which can appear over other apps right after a
            call ends. You can disable this feature at any time in Settings.
          </li>
          <li>
            <strong>Alarms, Background Run &amp; Boot Start:</strong> Used to
            deliver scheduled messages, call-back reminders, and the automatic
            old-message cleanup feature at the correct time — including after
            your device restarts.
          </li>
          <li>
            <strong>Location:</strong> Used to allow you to share your current
            location with your contacts within a conversation. Additionally,
            your approximate location (derived from IP address) may be used to
            support regionally relevant advertising. We do not use location to
            secretly track you or tag your messages.
          </li>
        </ul>

        <h2>3. Information Collected Automatically</h2>
        <p>
          While we never collect your message content, contacts, or call logs,
          we do collect limited technical and usage information to maintain and
          improve the Application:
        </p>
        <ul>
          <li>
            <strong>Device Information:</strong> Device model, operating system
            version, screen resolution, language, and application version.
          </li>
          <li>
            <strong>Usage Data / Analytics:</strong> Anonymous interaction data
            (e.g. which screens and features are used) and crash reports,
            collected via Firebase Analytics and Firebase Crashlytics, to
            identify bugs and improve app stability.
          </li>
          <li>
            <strong>Advertising Identifiers:</strong> Identifiers such as the
            Google Advertising ID (GAID) are used to support ad personalization
            and measure ad performance.
          </li>
          <li>
            <strong>Approximate Location:</strong> General location derived from
            your IP address, used solely for analytics and regional ad relevance
            (we do NOT request or access exact GPS location for this purpose).
          </li>
        </ul>

        <h2>4. Third-Party Services</h2>
        <p>
          We use the following trusted third-party services. None of them can
          access the content of your messages, contacts, or call logs:
        </p>
        <ul>
          <li>
            <strong>Google Analytics &amp; Crashlytics (Firebase):</strong>{" "}
            Collects anonymous usage data and crash reports. Learn more at the{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Google Privacy Policy
            </a>
            .
          </li>
          <li>
            <strong>Google AdMob:</strong> Displays advertisements within the
            Application and may collect your Advertising ID. Learn more at{" "}
            <a
              href="https://policies.google.com/technologies/ads"
              target="_blank"
              rel="noreferrer"
            >
              Google's Privacy &amp; Terms
            </a>
            .
          </li>
          <li>
            <strong>Google User Messaging Platform (UMP):</strong> Where legally
            required (e.g. in the EEA, UK, or under applicable US state laws),
            we use Google's UMP SDK to collect and manage your advertising
            consent choices. You can review or change your choice anytime from
            Settings &gt; Consent Revoke.
          </li>
          <li>
            <strong>Google Play In-App Updates:</strong> Used to check for and
            install app updates directly through Google Play.
          </li>
        </ul>

        <h2>5. How We Use the Information</h2>
        <p>
          The automatically collected technical data is used exclusively to:
        </p>
        <ul>
          <li>Operate, maintain, and improve the Application's stability.</li>
          <li>
            Serve relevant advertisements that support the free use of the app.
          </li>
          <li>Understand feature usage to guide future improvements.</li>
        </ul>

        <h2>6. Data Retention</h2>
        <p>
          We only retain data for as long as necessary to provide the
          Application's features:
        </p>
        <ul>
          <li>
            <strong>Local Data:</strong> Your messages, contacts, and call logs
            remain on your device until you manually delete them or uninstall
            the Application.
          </li>
          <li>
            <strong>Analytics &amp; Usage Data:</strong> Anonymous data
            collected via third-party services (like Firebase) is retained
            according to their respective privacy policies, typically for a
            period of up to 14 months, before being automatically deleted.
          </li>
        </ul>

        <h2>7. Data Security</h2>
        <p>
          We implement commercially reasonable security measures to protect your
          data. Since your messages, contacts, and call logs are processed
          entirely on your device and never transmitted to our servers, the risk
          of that data being intercepted through our systems is non-existent.
        </p>

        <h2>8. Your Rights, Choices &amp; Opt-Out (GDPR/CCPA)</h2>
        <p>
          Depending on your location, you may have specific rights regarding
          your personal data (such as the right to access, delete, or restrict
          processing). Because we process your messages and contacts entirely
          locally, you have full control over your data directly within the app:
        </p>
        <ul>
          <li>
            <strong>Delete Your Data:</strong> Since all personal data lives on
            your device, you can exercise your right to erasure simply by
            deleting conversations within the app or uninstalling the
            Application entirely.
          </li>
          <li>
            <strong>Revoke Ad Consent:</strong> Open Settings &gt; Consent
            Revoke within the Application at any time to review or change your
            advertising consent choice.
          </li>
          <li>
            <strong>Opt-out of Personalized Ads:</strong> You can also opt out
            via your Android Device Settings (Settings &gt; Google &gt; Ads &gt;
            Opt out of Ads Personalization) or by visiting{" "}
            <a
              href="https://adssettings.google.com/"
              target="_blank"
              rel="noreferrer"
            >
              Google Ad Settings
            </a>
            .
          </li>
          <li>
            <strong>Revoke Permissions:</strong> You can revoke Contacts,
            Camera, Call Log, Location, or any other permission at any time
            through your device settings, though this will limit the
            corresponding feature.
          </li>
        </ul>

        <h2>9. Children's Privacy</h2>
        <p>
          Our Application is not directed at children under the age of 13. We do
          not knowingly collect personal information from children. If we
          discover that data has been collected from a child under 13
          inadvertently through a third-party service, we will take steps to
          ensure it is deleted.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy periodically to reflect changes in
          our practices or regulatory requirements. We encourage you to review
          this page occasionally. Your continued use of the Application after
          changes are posted constitutes your acceptance of the new policy.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have any questions, concerns, or feedback regarding this
          Privacy Policy or our data practices, please contact us at:
        </p>
        <ul>
          <li>
            Developed By: <span>Aavakar Solution</span>
          </li>
          <li>
            By email:{" "}
            <a
              href="mailto:parth@aavakar.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              parth@aavakar.com
            </a>
          </li>
        </ul>
      </main>
    </div>
  );
};

export default MessagesPrivacyPolicy;
